import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import { Box, Breadcrumbs, Typography } from '@mui/material'
import { createFileRoute, Outlet } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useManualRequestDetailWorkflow, useManualRequestTabs } from '@/features/manual-requests'
import { ApprovalCommentDialog } from '@/features/manual-voucher-approval/ui/ApprovalCommentDialog'
import { CustomButton } from '@/shared/ui/button'
import { PageWrapper } from '@/shared/ui/page-wrapper'
import { CustomTab } from '@/shared/ui/tab'
import { CustomTabs } from '@/shared/ui/tabs'

export const Route = createFileRoute(
  '/_auth/applications/manual-requests/manual-request/$requestId'
)({
  component: RouteComponent,
})

function RouteComponent() {
  const { t } = useTranslation()
  const { requestId } = Route.useParams()

  const {
    canSubmit,
    canApproveOrReject,
    isSubmitting,
    isApproving,
    isRejecting,
    handleBack,
    handleSubmitForReview,
    handleApprove,
    handleReject,
    dialogOpen,
    dialogAction,
    dialogComment,
    setDialogComment,
    handleDialogClose,
    handleDialogConfirm,
  } = useManualRequestDetailWorkflow(requestId)

  const { activeTab, handleTabChange } = useManualRequestTabs(requestId)

  return (
    <PageWrapper title={t('Карточка заявки')}>
      <Box display="flex" gap={2} alignItems="center">
        <CustomButton
          size="small"
          variant="outlined"
          color="secondary"
          startIcon={<ArrowBackIos />}
          onClick={handleBack}
        >
          {t('Назад')}
        </CustomButton>
        <Breadcrumbs separator={<ArrowForwardIos sx={{ fontSize: 16 }} />}>
          <Typography color="text.secondary" key="1">
            {t('Заявки на доступ фиксации')}
          </Typography>
          <Typography key="2" color="text.primary">
            {t('Карточка заявки')}
          </Typography>
        </Breadcrumbs>
      </Box>
      <CustomTabs value={activeTab} variant="scrollable" scrollButtons="auto">
        <CustomTab onClick={() => handleTabChange('common')} value="common" label={t('Основное')} />
        <CustomTab
          onClick={() => handleTabChange('history')}
          value="history"
          label={t('История')}
        />
      </CustomTabs>
      <Outlet />
      {(canSubmit || canApproveOrReject) && (
        <Box
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            width: '100%',
            padding: '24px 0 24px 0',
            marginTop: 'auto',
            position: 'relative',
            zIndex: 1,
            backgroundColor: 'background.paper',
          }}
        >
          {canSubmit && (
            <CustomButton
              type="button"
              variant="contained"
              color="primary"
              onClick={handleSubmitForReview}
              loading={isSubmitting}
              disabled={isSubmitting || isApproving || isRejecting}
              sx={{ flexShrink: 0 }}
            >
              {t('Перевести статус в рассмотрении')}
            </CustomButton>
          )}
          {!canSubmit && canApproveOrReject && (
            <>
              <CustomButton
                type="button"
                variant="contained"
                color="primary"
                startIcon={<CheckIcon />}
                onClick={handleApprove}
                loading={isApproving}
                disabled={isSubmitting || isApproving || isRejecting}
                sx={{ flexShrink: 0 }}
              >
                {t('Одобрить')}
              </CustomButton>
              <CustomButton
                type="button"
                variant="contained"
                color="secondary"
                startIcon={<CloseIcon />}
                onClick={handleReject}
                loading={isRejecting}
                disabled={isSubmitting || isApproving || isRejecting}
                sx={{ flexShrink: 0 }}
              >
                {t('Отклонить')}
              </CustomButton>
            </>
          )}
        </Box>
      )}
      <ApprovalCommentDialog
        open={dialogOpen}
        action={dialogAction}
        comment={dialogComment}
        onCommentChange={setDialogComment}
        onClose={handleDialogClose}
        onConfirm={handleDialogConfirm}
        isLoading={isSubmitting || isApproving || isRejecting}
      />
    </PageWrapper>
  )
}
